import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  getGetMeQueryKey,
  useUpdatePhysician,
  useUpdateStaff,
} from "@elation/api-client/src/stitch-web";

import { authClient, UserEntity } from "@src/features/auth";
import {
  PhysicianEntity,
  PhysicianProfileUpdatePayloadSchema,
  useCurrentPhysician,
} from "@src/features/physicians";
import { PhysicianAccountSettingsPageV2 } from "@src/features/physicians/ui";
import {
  StaffEntity,
  StaffProfileUpdatePayloadSchema,
  useCurrentStaff,
} from "@src/features/staff";
import { StaffAccountSettingsPageV2 } from "@src/features/staff/ui";
import { navigationV2 } from "@src/shared/navigation/v2";
import { useActor } from "@src/shared/providers/actorProvider";

export function AccountSettingsV2Route() {
  const actor = useActor();
  const [isSendingVerificationEmail, setIsSendingVerificationEmail] =
    useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [verificationEmailError, setVerificationEmailError] = useState<
    string | null
  >(null);
  const email = actor.user.email ?? "";
  const isEmailVerified = UserEntity.isEmailVerified(actor.user);

  const sendVerificationEmail = useCallback(async () => {
    if (!email || isSendingVerificationEmail) return;

    setIsSendingVerificationEmail(true);
    setVerificationEmailError(null);

    try {
      const result = await authClient.sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}${navigationV2.settingsAccount()}`,
      });

      if (result.error) {
        throw new Error(
          result.error.message ?? "Unable to send verification email.",
        );
      }

      setVerificationEmailSent(true);
      toast.success("Verification email sent.");
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Unable to send verification email.";
      setVerificationEmailError(message);
      toast.error(message);
    } finally {
      setIsSendingVerificationEmail(false);
    }
  }, [email, isSendingVerificationEmail]);

  if (actor.type === "physician") {
    return (
      <PhysicianAccountSettingsRoute
        email={email}
        isEmailVerified={isEmailVerified}
        isSendingVerificationEmail={isSendingVerificationEmail}
        onSendVerificationEmail={sendVerificationEmail}
        verificationEmailError={verificationEmailError}
        verificationEmailSent={verificationEmailSent}
      />
    );
  }

  return (
    <StaffAccountSettingsRoute
      email={email}
      isEmailVerified={isEmailVerified}
      isSendingVerificationEmail={isSendingVerificationEmail}
      onSendVerificationEmail={sendVerificationEmail}
      verificationEmailError={verificationEmailError}
      verificationEmailSent={verificationEmailSent}
    />
  );
}

function PhysicianAccountSettingsRoute({
  email,
  isEmailVerified,
  isSendingVerificationEmail,
  onSendVerificationEmail,
  verificationEmailError,
  verificationEmailSent,
}: {
  email: string;
  isEmailVerified: boolean;
  isSendingVerificationEmail: boolean;
  onSendVerificationEmail: () => void | Promise<void>;
  verificationEmailError: string | null;
  verificationEmailSent: boolean;
}) {
  const physician = useCurrentPhysician();
  const queryClient = useQueryClient();
  const initialValues = useMemo(
    () => PhysicianEntity.toProfileFormValues(physician),
    [physician],
  );
  const updatePhysician = useUpdatePhysician();

  const isNpiVerified = PhysicianEntity.isNpiVerified(physician);
  const saveError =
    updatePhysician.error instanceof Error
      ? updatePhysician.error.message
      : updatePhysician.isError
        ? "Unable to save account settings."
        : null;

  const submit = useCallback(
    async (values: ReturnType<typeof PhysicianEntity.toProfileFormValues>) => {
      await updatePhysician.mutateAsync({
        id: physician.id,
        data: PhysicianProfileUpdatePayloadSchema.cast(values),
      });

      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success("Account information updated.");
    },
    [physician.id, queryClient, updatePhysician],
  );

  const verifyNpi = useCallback(
    async (npi: string) => {
      await updatePhysician.mutateAsync({
        id: physician.id,
        data: { npi },
      });

      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success("NPI verified.");
    },
    [physician.id, queryClient, updatePhysician],
  );

  return (
    <PhysicianAccountSettingsPageV2
      email={email}
      initialValues={initialValues}
      isEmailVerified={isEmailVerified}
      isNpiVerified={isNpiVerified}
      isSaving={updatePhysician.isPending}
      isSendingVerificationEmail={isSendingVerificationEmail}
      npi={physician.npi}
      onNpiVerified={verifyNpi}
      onSendVerificationEmail={onSendVerificationEmail}
      onSubmit={submit}
      saveError={saveError}
      verificationEmailError={verificationEmailError}
      verificationEmailSent={verificationEmailSent}
    />
  );
}

function StaffAccountSettingsRoute({
  email,
  isEmailVerified,
  isSendingVerificationEmail,
  onSendVerificationEmail,
  verificationEmailError,
  verificationEmailSent,
}: {
  email: string;
  isEmailVerified: boolean;
  isSendingVerificationEmail: boolean;
  onSendVerificationEmail: () => void | Promise<void>;
  verificationEmailError: string | null;
  verificationEmailSent: boolean;
}) {
  const staff = useCurrentStaff();
  const queryClient = useQueryClient();
  const initialValues = useMemo(
    () => StaffEntity.toProfileFormValues(staff),
    [staff],
  );
  const updateStaff = useUpdateStaff();

  const saveError =
    updateStaff.error instanceof Error
      ? updateStaff.error.message
      : updateStaff.isError
        ? "Unable to save account settings."
        : null;

  const submit = useCallback(
    async (values: ReturnType<typeof StaffEntity.toProfileFormValues>) => {
      await updateStaff.mutateAsync({
        id: staff.id,
        data: StaffProfileUpdatePayloadSchema.cast(values),
      });

      await queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
      toast.success("Account information updated.");
    },
    [staff.id, queryClient, updateStaff],
  );

  return (
    <StaffAccountSettingsPageV2
      email={email}
      initialValues={initialValues}
      isEmailVerified={isEmailVerified}
      isSendingVerificationEmail={isSendingVerificationEmail}
      isSaving={updateStaff.isPending}
      onSendVerificationEmail={onSendVerificationEmail}
      onSubmit={submit}
      saveError={saveError}
      verificationEmailError={verificationEmailError}
      verificationEmailSent={verificationEmailSent}
    />
  );
}
